import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Card, CardContent, Grid} from "@mui/material";
import {FormattedMessage} from "react-intl";
import {WMSContext} from "../../context/WMSContext";
import WmsDesktopLayout from "../../wmsDesktopLayout";
import WmsMobileLayout from "../../wmsMobileLayout";
import PageTitle from "components/WMS/Mobile/PageTitle";
import {getWmsStats, WmsStatsPeriod} from "api/wms/stats/stats";
import {blueColor, greyishBlue100, greyishBlue500} from "assets/jss/main";
import StatTile from "./components/StatTile";
import PeriodSelector from "./components/PeriodSelector";
import FlowChart from "./components/FlowChart";
import FlowTable from "./components/FlowTable";
import ShippedHistoryChart from "./components/ShippedHistoryChart";

const REFRESH_INTERVAL_MS = 60000
const FLOW_STEPS = ['picked', 'sorted', 'packed', 'shipped']
const SNAPSHOT_ORDER_TILES = [
    {key: 'ordersInProgress'},
    {key: 'ordersLate', hint: true, alert: true},
    {key: 'shipmentsToSort'}
]
const SNAPSHOT_ITEM_TILES = [
    {key: 'itemsTotal'},
    {key: 'toPick', statuses: ['new', 'pending', 'picking', 'not_found']},
    {key: 'toSort', statuses: ['in_trolley', 'ready_to_be_sorted']},
    {key: 'sorting', statuses: ['sorted']},
    {key: 'toPack', statuses: ['ready_to_be_packed', 'packing']},
    {key: 'packed', statuses: ['packed', 'ready_to_be_shipped']}
]

const sumStatuses = (items, statuses) => statuses.reduce((total, status) => total + (items?.[status] ?? 0), 0)

function SectionTitle({id, action = null}) {
    return (
        <Grid container justifyContent={"space-between"} alignItems={"center"}>
            <span style={{fontWeight: "bold", color: blueColor}}><FormattedMessage id={id}/></span>
            {action}
        </Grid>
    )
}

function SectionCard({children}) {
    return (
        <Card sx={{boxShadow: 0, border: "1px solid " + greyishBlue100, borderRadius: "8px"}}>
            <CardContent>{children}</CardContent>
        </Card>
    )
}

function WMSStats() {
    const {currentInventory, isMobile} = useContext(WMSContext)

    const [period, setPeriod] = useState(WmsStatsPeriod.TODAY)
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const load = useCallback(() => {
        if (!currentInventory) return

        getWmsStats(currentInventory.id, period, timezone).then(response => {
            setStats(response)
            setError(false)
        }).catch(() => {
            setError(true)
        }).finally(() => {
            setLoading(false)
        })
    }, [currentInventory, period, timezone])

    useEffect(() => {
        setLoading(true)
        load()
        const interval = setInterval(load, REFRESH_INTERVAL_MS)

        return () => clearInterval(interval)
    }, [load])

    const snapshot = stats?.snapshot
    const items = snapshot?.items
    const lateAfterDays = snapshot?.lateAfterDays ?? 3
    const flow = stats?.flow
    const flowRows = period === WmsStatsPeriod.TODAY ? (flow?.byHour ?? []) : (flow?.byDay ?? [])

    const content = (
        <Grid container direction={"column"} gap={"16px"}>
            <PageTitle
                title={<FormattedMessage id={"wms.admin_wms_stats.title"}/>}
                subtitle={<FormattedMessage id={"wms.admin_wms_stats.subtitle"}/>}
                isDesktop={!isMobile}
            />

            {error && (
                <span style={{color: greyishBlue500}}><FormattedMessage id={"wms.admin_wms_stats.error"}/></span>
            )}

            <SectionCard>
                <Grid container direction={"column"} gap={"12px"}>
                    <SectionTitle id={"wms.admin_wms_stats.snapshot.title"}/>
                    <Grid container gap={"12px"}>
                        {SNAPSHOT_ORDER_TILES.map(tile => (
                            <StatTile
                                key={tile.key}
                                loading={loading}
                                label={<FormattedMessage id={`wms.admin_wms_stats.snapshot.${tile.key}`}/>}
                                tooltip={<FormattedMessage id={`wms.admin_wms_stats.snapshot.${tile.key}.tooltip`} values={{days: lateAfterDays}}/>}
                                value={snapshot?.[tile.key]}
                                hint={tile.hint ? <FormattedMessage id={`wms.admin_wms_stats.snapshot.${tile.key}.hint`} values={{days: lateAfterDays}}/> : null}
                                alert={tile.alert}
                            />
                        ))}
                    </Grid>
                    <Grid container gap={"12px"}>
                        {SNAPSHOT_ITEM_TILES.map(tile => (
                            <StatTile
                                key={tile.key}
                                loading={loading}
                                label={<FormattedMessage id={`wms.admin_wms_stats.snapshot.${tile.key}`}/>}
                                tooltip={<FormattedMessage id={`wms.admin_wms_stats.snapshot.${tile.key}.tooltip`}/>}
                                value={tile.statuses ? sumStatuses(items, tile.statuses) : snapshot?.itemsTotal}
                            />
                        ))}
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard>
                <Grid container direction={"column"} gap={"12px"}>
                    <SectionTitle id={"wms.admin_wms_stats.flow.title"} action={<PeriodSelector period={period} onChange={setPeriod}/>}/>
                    <Grid container gap={"12px"}>
                        {FLOW_STEPS.map(step => (
                            <StatTile
                                key={step}
                                loading={loading}
                                label={<FormattedMessage id={`wms.admin_wms_stats.flow.${step}`}/>}
                                value={flow?.totals?.[step]}
                                previousValue={flow?.previous?.totals?.[step] ?? null}
                                hint={<FormattedMessage id={`wms.admin_wms_stats.flow.previous.${period}`}/>}
                                tooltip={<FormattedMessage id={`wms.admin_wms_stats.flow.${step}.tooltip`}/>}
                            />
                        ))}
                    </Grid>
                    {flow && (
                        <>
                            <FlowChart rows={flowRows} period={period}/>
                            <FlowTable rows={flowRows} totals={flow.totals} period={period}/>
                        </>
                    )}
                </Grid>
            </SectionCard>

            <SectionCard>
                <Grid container direction={"column"} gap={"12px"}>
                    <SectionTitle id={"wms.admin_wms_stats.shippedHistory.title"}/>
                    {stats && <ShippedHistoryChart rows={stats.shippedHistory}/>}
                </Grid>
            </SectionCard>
        </Grid>
    )

    if (isMobile) {
        return (
            <WmsMobileLayout>
                <WmsMobileLayout.LeftHeader></WmsMobileLayout.LeftHeader>
                <WmsMobileLayout.RightHeader></WmsMobileLayout.RightHeader>
                <WmsMobileLayout.Content>
                    {content}
                </WmsMobileLayout.Content>
            </WmsMobileLayout>
        )
    }

    return (
        <WmsDesktopLayout customStyle={{minWidth: 0}}>
            <WmsDesktopLayout.Content customStyle={{minWidth: 0, overflow: 'auto'}}>
                {content}
            </WmsDesktopLayout.Content>
        </WmsDesktopLayout>
    );
}

export default WMSStats;
